import { Alert, Badge, Loader, Stack, Table, Text, Title } from '@mantine/core';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';

import { AdminShell } from '../../components/admin-shell/AdminShell';
import { adminGet } from './_api';

type SweeperRow = {
  name: string;
  lastRunAt: string | null;
  durationMs?: number;
  ok?: boolean;
  error?: string;
};

type SweepersList = { items: SweeperRow[] };

export const Route = createFileRoute('/admin/sweepers')({
  component: AdminSweepers,
});

function AdminSweepers() {
  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'sweepers'],
    queryFn: () => adminGet<SweepersList>('/api/admin/sweepers'),
    refetchInterval: 60_000,
  });

  return (
    <AdminShell>
      <Stack gap="md">
        <Stack gap={4}>
          <Title order={2}>Sweepers</Title>
          <Text c="dimmed" size="sm">
            Each cron-driven sweeper writes its last-run record to KV after it
            settles. Records expire after 30 days; a row showing &ldquo;Never&rdquo;
            either hasn&apos;t fired since deploy or hasn&apos;t fired in 30+ days.
          </Text>
        </Stack>

        {isLoading && <Loader size="sm" />}

        {data && data.items.some((r) => r.ok === false) && (
          <Alert color="red" variant="light">
            One or more sweepers failed on their last run. Check the row below
            and the Worker logs for the trace.
          </Alert>
        )}

        {data && (
          <Table striped highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Sweeper</Table.Th>
                <Table.Th>Last run</Table.Th>
                <Table.Th>Duration</Table.Th>
                <Table.Th>Status</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {data.items.map((r) => (
                <Table.Tr key={r.name}>
                  <Table.Td>
                    <code>{r.name}</code>
                  </Table.Td>
                  <Table.Td>
                    {r.lastRunAt === null ? (
                      <Text size="sm" c="dimmed">
                        Never
                      </Text>
                    ) : (
                      <Text size="sm">{relative(r.lastRunAt)}</Text>
                    )}
                  </Table.Td>
                  <Table.Td>
                    {r.durationMs === undefined ? (
                      <Text size="sm" c="dimmed">
                        –
                      </Text>
                    ) : (
                      <Text size="sm">{formatDuration(r.durationMs)}</Text>
                    )}
                  </Table.Td>
                  <Table.Td>
                    {r.lastRunAt === null ? (
                      <Badge variant="light" color="gray">
                        idle
                      </Badge>
                    ) : r.ok === false ? (
                      <Badge color="red" variant="light">
                        failed
                      </Badge>
                    ) : isStale(r.lastRunAt, r.name) ? (
                      <Badge color="yellow" variant="light">
                        stale
                      </Badge>
                    ) : (
                      <Badge color="tide" variant="light">
                        ok
                      </Badge>
                    )}
                    {r.error && (
                      <Text size="xs" c="red.7" mt={2}>
                        {r.error}
                      </Text>
                    )}
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        )}
      </Stack>
    </AdminShell>
  );
}

const HOURLY_SWEEPERS = new Set([
  'exchange-completion',
  'exchange-no-response',
  'dispute-sla-monitor',
]);

function isStale(iso: string, name: string): boolean {
  const age = Date.now() - new Date(iso).getTime();
  // Hourly sweepers are stale after 90 minutes; daily after 26 hours.
  const limit = HOURLY_SWEEPERS.has(name) ? 90 * 60 * 1000 : 26 * 60 * 60 * 1000;
  return age > limit;
}

function relative(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  const minutes = Math.floor(ms / (60 * 1000));
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

function formatDuration(ms: number): string {
  if (ms < 1000) return `${ms} ms`;
  return `${(ms / 1000).toFixed(2)} s`;
}
